import { useQuery } from '@tanstack/react-query'
import { BarChart3 } from 'lucide-react'
import { api } from '@/lib/api-client'
import { formatDateShort } from '@/lib/utils'
import { PageHeader } from '@/components/ui/page-header'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { formatCurrency } from '@roi365/shared'
import { useState } from 'react'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Label,
} from 'recharts'

interface TrendPoint {
  snapshot_date: string
  total_users: number
  licensed_users: number
  active_users_30d: number
  total_licenses_purchased: number
  total_licenses_assigned: number
  estimated_monthly_cost: number
  estimated_monthly_waste: number
}

interface SavingsEvolution {
  series: { snapshot_date: string; cumulative_applied_saving: number }[]
  currentPendingSaving: number
}

export function HistoryPage() {
  const [days, setDays] = useState(30)

  const { data: trends, isLoading } = useQuery({
    queryKey: ['dashboard', 'trends', days],
    queryFn: () => api.get<TrendPoint[]>(`/dashboard/trends?days=${days}`),
  })

  const { data: savings } = useQuery({
    queryKey: ['dashboard', 'savings-evolution', days],
    queryFn: () => api.get<SavingsEvolution>(`/dashboard/savings-evolution?days=${days}`),
  })

  const chartData = (trends ?? []).map((t) => ({
    ...t,
    date: formatDateShort(t.snapshot_date),
  }))

  const savingsChartData = (savings?.series ?? []).map((s) => ({
    date: formatDateShort(s.snapshot_date),
    acumulado: Math.round(s.cumulative_applied_saving * 100) / 100,
  }))

  return (
    <div className="space-y-6">
      <PageHeader
        title="Historico"
        description="Tendencias de uso, costes y licencias a lo largo del tiempo"
        actions={
          <div className="flex gap-2">
            {[7, 30, 90, 365].map((d) => (
              <Button key={d} variant={days === d ? 'primary' : 'secondary'} size="sm" onClick={() => setDays(d)}>
                {d === 365 ? '1 año' : `${d}d`}
              </Button>
            ))}
          </div>
        }
      />

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-72 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-200)' }} />
          ))}
        </div>
      ) : !chartData.length ? (
        <Card>
          <EmptyState
            icon={BarChart3}
            title="Sin datos historicos"
            description="Los datos se generan automaticamente con cada sincronizacion diaria"
          />
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader><CardTitle>Evolucion del Ahorro Acumulado</CardTitle></CardHeader>
            <p className="mb-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Suma del ahorro proyectado de cada recomendacion aplicada (monthly_saving x meses hasta vencimiento de la licencia).
              La linea discontinua marca el ahorro potencial de las recomendaciones pendientes a dia de hoy.
            </p>
            {savingsChartData.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={savingsChartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="var(--color-text-tertiary)" />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      stroke="var(--color-text-tertiary)"
                      tickFormatter={(v) => `${v}€`}
                      domain={[0, (dataMax: number) => Math.ceil(Math.max(dataMax, savings?.currentPendingSaving ?? 0) * 1.1)]}
                    />
                    <Tooltip formatter={(value: number, name: string) => [formatCurrency(value), name]} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="acumulado"
                      name="Ahorro acumulado proyectado"
                      stroke="var(--color-success)"
                      fill="var(--color-success-light)"
                      strokeWidth={2}
                    />
                    {(savings?.currentPendingSaving ?? 0) > 0 && (
                      <ReferenceLine
                        y={savings!.currentPendingSaving}
                        stroke="var(--color-warning)"
                        strokeDasharray="6 4"
                        strokeWidth={2}
                        ifOverflow="extendDomain"
                      >
                        <Label
                          value={`Potencial pendiente: ${formatCurrency(savings!.currentPendingSaving)}`}
                          position="insideTopRight"
                          style={{ fill: 'var(--color-warning)', fontSize: 12, fontWeight: 600 }}
                        />
                      </ReferenceLine>
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="py-12 text-center text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                Aun no hay recomendaciones aplicadas en este periodo
              </p>
            )}
          </Card>

          <Card>
            <CardHeader><CardTitle>Evolucion de Costes</CardTitle></CardHeader>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="var(--color-text-tertiary)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="var(--color-text-tertiary)" tickFormatter={(v) => `${v}€`} />
                  <Tooltip formatter={(value: number, name: string) => [formatCurrency(value), name]} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="estimated_monthly_cost"
                    name="Coste mensual"
                    stroke="var(--color-brand-primary)"
                    fill="var(--color-brand-primary-50)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="estimated_monthly_waste"
                    name="Desperdicio mensual estimado"
                    stroke="var(--color-warning)"
                    fill="var(--color-warning-light)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card>
            <CardHeader><CardTitle>Usuarios y Actividad</CardTitle></CardHeader>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="var(--color-text-tertiary)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="var(--color-text-tertiary)" />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="licensed_users" name="Usuarios con licencia" stroke="var(--color-brand-primary)" fill="var(--color-brand-primary-50)" strokeWidth={2} />
                  <Area type="monotone" dataKey="active_users_30d" name="Activos (30d)" stroke="var(--color-brand-accent-2)" fill="rgba(60,219,192,0.1)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card>
            <CardHeader><CardTitle>Licencias</CardTitle></CardHeader>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="var(--color-text-tertiary)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="var(--color-text-tertiary)" />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="total_licenses_purchased" name="Compradas" stroke="var(--color-brand-secondary)" fill="var(--color-brand-secondary-50)" strokeWidth={2} />
                  <Area type="monotone" dataKey="total_licenses_assigned" name="Asignadas" stroke="var(--color-brand-accent-1)" fill="rgba(5,195,222,0.1)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </>
      )}
    </div>
  )
}
